import React from 'react'
import gql from 'graphql-tag'
import { defaultDataIdFromObject } from 'apollo-cache-inmemory'
import { Query, Mutation } from 'react-apollo'
import { PureQueryOptions } from 'apollo-client'
import { ContrastFormattedMessage } from '../../contrast-formatted-message'
import { excludeFrom, TagDialog } from '../../../legacy-design-system/tags'

const availableTagsQuery = gql`
  query AvailableApplicationTags($organizationID: String!) {
    organizationApplicationTags(organizationID: $organizationID) {
      tags
    }
  }
`

interface ModifyTagProps {
  add: string[]
  remove: string[]
}

interface DialogProps {
  isOpen: boolean
  closeDialog: () => void
  initialTags: string[]
  organizationID: string
}

interface MutationProps {
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
  applicationIDs: string[]
}

function ModifyApplicationTags({
  isOpen,
  closeDialog,
  initialTags,
  organizationID,
  modifyTags,
  multipleApplications
}: DialogProps & {
  multipleApplications: boolean
  modifyTags: (props: ModifyTagProps) => Promise<any>
}) {
  return (
    <Query query={availableTagsQuery} variables={{ organizationID }}>
      {(result) => {
        const { data, error, loading, refetch } = result
        const availableTags =
          error || loading ? [] : data.organizationApplicationTags.tags

        return (
          <ContrastFormattedMessage
            id={
              multipleApplications
                ? 'title_applications_tagApplications'
                : 'title_applicationDashboard_tagApplication'
            }
            defaultMessage={
              multipleApplications ? 'Tag Applications' : 'Tag Application'
            }
          >
            {(title) => (
              <TagDialog
                initialAvailableTags={availableTags}
                initialSelectedTags={initialTags}
                onConfirm={(selectedTags) => {
                  const add = excludeFrom(selectedTags, initialTags)
                  const remove = excludeFrom(initialTags, selectedTags)

                  if (add.length + remove.length > 0) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    modifyTags({ add, remove }).then(refetch)
                  }

                  closeDialog()
                }}
                onCancel={closeDialog}
                title={title}
                isOpen={isOpen}
              />
            )}
          </ContrastFormattedMessage>
        )
      }}
    </Query>
  )
}

const applicationTagsFragment = gql`
  fragment application on Application {
    tags
  }
`

function updateTags(applicationIDs: string[], add: string[], remove: string[]) {
  return (proxy) => {
    applicationIDs.forEach((applicationID) => {
      const id =
        defaultDataIdFromObject({
          __typename: 'Application',
          id: applicationID
        }) || ''

      const { tags }: any = proxy.readFragment({
        id,
        fragment: applicationTagsFragment
      })

      proxy.writeFragment({
        id,
        fragment: applicationTagsFragment,
        data: {
          tags: tags.filter((t) => remove.every((r) => r !== t)).concat(add),
          __typename: 'Application'
        }
      })
    })
  }
}

function getOptimisticResponse(fieldName) {
  return {
    [fieldName]: {
      _placeholder: true,
      __typename: 'ApplicationBulkResponse'
    },
    __typename: 'Mutation'
  }
}

class ModifyTagsMutation extends Mutation<
  any,
  ModifyTagProps & {
    organizationID: string
    ids: string[]
  }
> {}

const modifyApplicationTags = gql`
  mutation modifyApplicationTags(
    $organizationID: ID!
    $add: [String!]
    $remove: [String!]
    $ids: [String!]!
  ) {
    modifyApplicationTags(
      input: {
        organizationID: $organizationID
        add: $add
        remove: $remove
        ids: $ids
      }
    ) {
      _placeholder
    }
  }
`

export function ModifyApplicationTagsDialog({
  organizationID,
  applicationIDs,
  refetchQueries,
  refresh,
  initialTags,
  closeDialog,
  isOpen
}: DialogProps & MutationProps) {
  return (
    <ModifyTagsMutation mutation={modifyApplicationTags} ignoreResults>
      {(mutate) => {
        return (
          <ModifyApplicationTags
            isOpen={isOpen}
            initialTags={initialTags}
            closeDialog={closeDialog}
            multipleApplications={applicationIDs.length > 1}
            organizationID={organizationID}
            modifyTags={({ add, remove }) =>
              mutate({
                refetchQueries,
                variables: { add, remove, organizationID, ids: applicationIDs },
                optimisticResponse: getOptimisticResponse(
                  'modifyApplicationTags'
                ),
                update: updateTags(applicationIDs, add, remove)
              }).then(refresh)
            }
          />
        )
      }}
    </ModifyTagsMutation>
  )
}

class ModifyTagsByFilterMutation extends Mutation<
  any,
  ModifyTagProps & {
    organizationID: string
    excludedApplications: string[]
    filters: any
  }
> {}

const modifyApplicationTagsByFilter = gql`
  mutation ModifyApplicationTagsByFilters(
    $organizationID: ID!
    $add: [String!]!
    $remove: [String!]!
    $filters: ApplicationFilterArg!
    $excludedApplications: [String!]!
  ) {
    modifyApplicationTagsByFilters(
      input: {
        organizationID: $organizationID
        add: $add
        remove: $remove
        filters: $filters
        excludedApplications: $excludedApplications
      }
    ) {
      _placeholder
    }
  }
`

export function ModifyApplicationTagsByFilterDialog({
  filters,
  organizationID,
  excludedApplications,
  refresh,
  refetchQueries,
  initialTags,
  closeDialog,
  isOpen,
  applicationIDs
}: DialogProps &
  MutationProps & {
    organizationID: string
    excludedApplications: string[]
    filters: any
  }) {
  return (
    <ModifyTagsByFilterMutation mutation={modifyApplicationTagsByFilter}>
      {(mutate) => {
        return (
          <ModifyApplicationTags
            initialTags={initialTags}
            closeDialog={closeDialog}
            isOpen={isOpen}
            multipleApplications={true}
            organizationID={organizationID}
            modifyTags={({ add, remove }) =>
              mutate({
                refetchQueries,
                variables: {
                  add,
                  remove,
                  filters,
                  organizationID,
                  excludedApplications
                },
                optimisticResponse: getOptimisticResponse(
                  'modifyApplicationTagsByFilters'
                ),
                update: updateTags(applicationIDs, add, remove)
              }).then(refresh)
            }
          />
        )
      }}
    </ModifyTagsByFilterMutation>
  )
}
